// DesktopLayout — the >= 1024px property-detail composition. Top bar
// with breadcrumb + Lịch sử / Xoá / Edit / Đặt lịch xem CTAs, then a
// 1.5fr:1fr split: gallery left (DesktopGallery), details right. The
// change history opens as a modal from the top bar's "Lịch sử" button
// (ActivityHistoryDialog) — it used to be an inline timeline pinned at
// the bottom of the right column.

import { useState } from "react";
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Sliders, X } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/features/property/utils/format-price";
import { propertyTypeLabel } from "@/features/property/utils/property-enum-options";
import {
  pricePerSqmLabel,
  propertyTypeBadgeText,
} from "@/features/property/utils/property-display";
import { useCanManageProperty } from "@/features/property/hooks/use-can-manage-property";
import { PropertyFreshnessBanner } from "@/features/property/components/property-freshness-banner";
import { SpecsGrid } from "@/features/property/components/detail/specs-grid";
import { ProjectInfoCard } from "@/features/property/components/detail/project-info-card";
import { DescriptionBlock } from "@/features/property/components/detail/description-block";
import { BrokerCard } from "@/features/property/components/detail/broker-card";
import { ActionGrid } from "@/features/property/components/detail/action-grid";
import ActivityHistoryDialog from "@/features/activity/activity-history-dialog";
import { DesktopGallery } from "@/features/property/components/detail/desktop-gallery";
import { DeletePropertyConfirmDialog } from "@/features/property/components/delete-property-confirm-dialog";
import type { Property, PropertyImage } from "@/types/property";

interface DesktopLayoutProps {
  property: Property;
  images: PropertyImage[];
  onBack: () => void;
  onOpenAi: () => void;
  onOpenSchedule: () => void;
}

export function DesktopLayout({
  property,
  images,
  onBack,
  onOpenAi,
  onOpenSchedule,
}: DesktopLayoutProps) {
  const title = property.buildingName ?? "Bất động sản";
  const pricePerSqm = pricePerSqmLabel(property);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const canManage = useCanManageProperty(property);

  return (
    <div className="flex h-svh flex-col overflow-hidden">
      {/* Top app bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-card px-8 py-3.5">
        <div className="flex items-center gap-3 text-[14px]">
          <button
            type="button"
            onClick={onBack}
            aria-label="Quay lại"
            className="inline-flex size-8 items-center justify-center rounded-lg border border-border bg-card hover:bg-muted"
          >
            <ChevronLeft aria-hidden="true" className="size-3.5" />
          </button>
          <span className="text-muted-foreground">BĐS</span>
          <ChevronRight aria-hidden="true" className="size-3 text-ink-faint" />
          {property.internalCode !== null ? (
            <>
              <span className="font-mono text-muted-foreground">
                {property.internalCode}
              </span>
              <ChevronRight aria-hidden="true" className="size-3 text-ink-faint" />
            </>
          ) : null}
          <span className="truncate font-semibold">{title}</span>
        </div>
        <div className="flex gap-2">
          {/* History is a read action — available to everyone who can view
              the property, so it sits outside the canManage gate. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen(true)}
            className="rounded-lg"
          >
            <Clock aria-hidden="true" className="size-3" />
            Lịch sử
          </Button>
          {canManage ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                className="rounded-lg border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
              >
                <X aria-hidden="true" className="size-3" />
                Xoá
              </Button>
              <Button asChild variant="outline" size="sm" className="rounded-lg">
                <Link to={`/properties/${property.id}/edit`}>
                  <Sliders aria-hidden="true" className="size-3" />
                  Chỉnh sửa
                </Link>
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            onClick={onOpenSchedule}
            size="sm"
            className="rounded-lg bg-foreground text-background hover:bg-foreground/90"
          >
            <Calendar aria-hidden="true" className="size-3" />
            Yêu cầu xem nhà
          </Button>
        </div>
      </div>

      {/* Two-column body — page is locked to viewport; each column manages
          its own overflow. Single-column fallback for narrow viewports
          mirrors the mobile flow's natural scrolling. */}
      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1.5fr_1fr]">
        {/* LEFT — gallery column. flex-col so the hero image absorbs the
            remaining vertical space and the 80px thumbnail strip sits at
            the bottom. */}
        <main className="flex flex-col gap-2 overflow-hidden p-6">
          <DesktopGallery
            property={property}
            images={images}
            quickStatusReadOnly={!canManage}
          />
        </main>

        {/* RIGHT — details. Outer column locked to grid row; inner content
            stack scrolls if it overflows. */}
        <aside className="flex flex-col overflow-hidden border-l border-border bg-card">
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3.5 px-6 py-5">
              {/* Badge row + title + location.
                  Order: internal code (mono) · propertyType (when present) ·
                  listing type. Property type may be null on list-derived
                  records — skip the middle badge in that case. */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {property.internalCode !== null ? (
                    <Badge variant="neutral" className="font-mono text-[11px]">
                      {property.internalCode}
                    </Badge>
                  ) : null}
                  {property.propertyType !== null ? (
                    <Badge variant="info" className="text-[11px]">
                      {propertyTypeLabel(property.propertyType)}
                    </Badge>
                  ) : null}
                  <Badge variant="secondary" className="text-[11px]">
                    {propertyTypeBadgeText(property)}
                  </Badge>
                </div>
                <h1 className="text-[28px] leading-tight font-bold tracking-tight text-balance">
                  {title}
                </h1>
                <div className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <MapPin aria-hidden="true" className="size-3" />
                  <span>
                    {property.wardDisplayName}
                    {property.floor !== null ? ` · Tầng ${property.floor}` : ""}
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2.5 border-t border-border pt-3">
                <span className="font-mono text-3xl leading-none font-bold tracking-tight tabular-nums">
                  {formatPrice(property.price, property.listingType)}
                </span>
                {pricePerSqm !== null ? (
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {pricePerSqm}
                  </span>
                ) : null}
              </div>

              {/* Freshness banner */}
              <PropertyFreshnessBanner
                propertyId={property.id}
                lastFreshnessConfirmedAt={property.lastFreshnessConfirmedAt}
                canConfirm={canManage}
              />

              {/* Specs grid */}
              <SpecsGrid property={property} surface="background" />

              {/* Project (building) info — only when the unit is linked. */}
              <ProjectInfoCard property={property} />

              {/* Description */}
              {property.description !== null && property.description.trim().length > 0 ? (
                <DescriptionBlock description={property.description} />
              ) : null}

              {/* Action grid (3 equal columns) */}
              <ActionGrid property={property} onOpenAi={onOpenAi} />

            </div>
          </div>

          {/* Nguồn phụ trách — pinned at the foot of the details column so the
              Source contact stays visible while the listing facts scroll
              above it. */}
          <div className="shrink-0 border-t border-border bg-background px-6 py-3.5">
            <BrokerCard property={property} variant="desktop" />
          </div>
        </aside>
      </div>

      <DeletePropertyConfirmDialog
        property={property}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
      <ActivityHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityType="PROPERTY"
        entityId={property.id}
        headerLabel={property.internalCode ?? `BĐS #${property.id}`}
        headerTitle={property.buildingName ?? "Bất động sản"}
      />
    </div>
  );
}
